import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';
import { showItems, trickItems, videoItems } from '../data/portfolio';
import { fadeIn } from '../variants';

const AUTOPLAY_DELAY = 3550;
const SWIPE_DISTANCE_THRESHOLD = 48;
const SWIPE_VERTICAL_TOLERANCE = 1.15;

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
  }),
  center: {
    x: '0%',
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
  }),
};

const getImageClassName = (mobileImageMode) => {
  if (mobileImageMode === 'contain') {
    return 'h-full w-full bg-primary object-contain object-center md:object-contain md:object-center';
  }

  if (mobileImageMode === 'shift-left') {
    return 'h-full w-full bg-primary object-cover object-[70%_center] md:object-contain md:object-center';
  }

  if (mobileImageMode === 'focus-right') {
    return 'h-full w-full bg-primary object-cover object-[68%_center] md:object-contain md:object-center';
  }

  if (mobileImageMode === 'crop-y') {
    return 'h-full w-full bg-primary object-cover object-[center_45%] md:object-contain md:object-center';
  }

  return 'h-full w-full bg-primary object-cover object-center md:object-contain md:object-center';
};

const MediaCarousel = ({
  items,
  label,
  description,
  sectionId,
  autoPlay = true,
  selectedItemRequest,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [activityKey, setActivityKey] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const sectionRef = useRef(null);
  const handledSelectionRequestId = useRef(null);
  const swipeStartRef = useRef(null);
  const item = items[activeIndex];

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const preloadedImages = items
      .filter((mediaItem) => mediaItem.type === 'image')
      .map((mediaItem) => {
        const image = new window.Image();
        image.decoding = 'async';
        image.src = mediaItem.image;
        return image;
      });

    return () => {
      preloadedImages.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, [items]);

  useEffect(() => {
    if (!autoPlay || items.length <= 1 || isHovering) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDirection(1);
      setActiveIndex((index) => (index === items.length - 1 ? 0 : index + 1));
    }, AUTOPLAY_DELAY);

    return () => window.clearTimeout(timeoutId);
  }, [activeIndex, activityKey, autoPlay, isHovering, items.length]);

  useEffect(() => {
    if (
      !selectedItemRequest ||
      handledSelectionRequestId.current === selectedItemRequest.requestId
    ) {
      return;
    }

    const selectedIndex = items.findIndex(
      (mediaItem) => mediaItem.id === selectedItemRequest.itemId,
    );

    if (selectedIndex === -1) {
      return;
    }

    handledSelectionRequestId.current = selectedItemRequest.requestId;
    setDirection(selectedIndex >= activeIndex ? 1 : -1);
    setActiveIndex(selectedIndex);
    setActivityKey((key) => key + 1);
    setIsHovering(false);

    if (typeof window !== 'undefined') {
      const sectionHash = `#${sectionId}`;

      if (window.location.hash !== sectionHash) {
        window.history.pushState(null, '', sectionHash);
      }

      window.requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [activeIndex, items, sectionId, selectedItemRequest]);

  const registerActivity = () => {
    setActivityKey((key) => key + 1);
  };

  const showPrevious = () => {
    registerActivity();
    setDirection(-1);
    setActiveIndex((index) => (index === 0 ? items.length - 1 : index - 1));
  };

  const showNext = () => {
    registerActivity();
    setDirection(1);
    setActiveIndex((index) => (index === items.length - 1 ? 0 : index + 1));
  };

  const handleTouchStart = (event) => {
    registerActivity();

    if (items.length <= 1 || event.touches.length !== 1) {
      swipeStartRef.current = null;
      return;
    }

    const touch = event.touches[0];
    swipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  const handleTouchEnd = (event) => {
    if (!swipeStartRef.current || event.changedTouches.length !== 1) {
      swipeStartRef.current = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    swipeStartRef.current = null;

    if (
      absDeltaX < SWIPE_DISTANCE_THRESHOLD ||
      absDeltaX < absDeltaY * SWIPE_VERTICAL_TOLERANCE
    ) {
      return;
    }

    if (deltaX < 0) {
      showNext();
      return;
    }

    showPrevious();
  };

  const handleTouchCancel = () => {
    swipeStartRef.current = null;
  };

  const handleSwipeZoneTouchStart = (event) => {
    event.stopPropagation();
    handleTouchStart(event);
  };

  const handleSwipeZoneTouchEnd = (event) => {
    event.stopPropagation();
    handleTouchEnd(event);
  };

  const counter = (
    <>
      {String(activeIndex + 1).padStart(2, '0')} /{' '}
      {String(items.length).padStart(2, '0')}
    </>
  );

  const controls = (
    <div className='flex items-center gap-3'>
      {item.sourceUrl && (
        <a
          className='pill-button h-12 px-5'
          href={item.sourceUrl}
          target='_blank'
          rel='noreferrer'
          onClick={registerActivity}
        >
          Source <FiExternalLink size={17} />
        </a>
      )}
      <button
        type='button'
        className='icon-button h-12 w-12 rounded-[8px] bg-primary/80'
        onClick={showPrevious}
        aria-label={`Previous ${label.toLowerCase()}`}
        title={`Previous ${label.toLowerCase()}`}
      >
        <FiChevronLeft size={24} />
      </button>
      <button
        type='button'
        className='icon-button h-12 w-12 rounded-[8px] bg-primary/80'
        onClick={showNext}
        aria-label={`Next ${label.toLowerCase()}`}
        title={`Next ${label.toLowerCase()}`}
      >
        <FiChevronRight size={24} />
      </button>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      id={sectionId}
      className='page-section scroll-mt-20 flex items-center'
    >
      <div className='section-shell'>
        <motion.div
          variants={fadeIn('up', 0.1)}
          initial={false}
          whileInView='show'
          viewport={{ once: true, amount: 0.25 }}
          className='mb-10'
        >
          <p className='section-kicker'>{label}</p>
          <h2 className='section-title'>{description}</h2>
        </motion.div>

        <motion.div
          variants={fadeIn('up', 0.12)}
          initial={false}
          whileInView='show'
          viewport={{ once: true, amount: 0.25 }}
          className='relative min-h-[520px] touch-pan-y overflow-hidden rounded-[8px] border border-white/10 bg-panel md:min-h-[680px]'
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={`${sectionId}-${activeIndex}-${item.title}`}
              custom={direction}
              variants={slideVariants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className='absolute inset-0'
            >
              {item.type === 'image' ? (
                <div className='absolute left-1/2 top-0 h-full aspect-square -translate-x-1/2 bg-primary md:static md:h-full md:w-full md:translate-x-0 md:aspect-auto'>
                  <img
                    className={getImageClassName(item.mobileImageMode)}
                    src={item.image}
                    alt={item.title}
                    decoding='async'
                  />
                </div>
              ) : (
                <div className='relative h-full w-full bg-black'>
                  <iframe
                    className='h-full w-full bg-black'
                    src={item.embedUrl}
                    title={item.title}
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                    allowFullScreen
                    loading='lazy'
                  />
                  <button
                    type='button'
                    tabIndex={-1}
                    className='absolute inset-y-0 left-0 z-10 w-[28%] appearance-none bg-transparent p-0 md:hidden'
                    aria-label={`Swipe previous ${label.toLowerCase()}`}
                    onTouchStart={handleSwipeZoneTouchStart}
                    onTouchEnd={handleSwipeZoneTouchEnd}
                    onTouchCancel={handleTouchCancel}
                  />
                  <button
                    type='button'
                    tabIndex={-1}
                    className='absolute inset-y-0 right-0 z-10 w-[28%] appearance-none bg-transparent p-0 md:hidden'
                    aria-label={`Swipe next ${label.toLowerCase()}`}
                    onTouchStart={handleSwipeZoneTouchStart}
                    onTouchEnd={handleSwipeZoneTouchEnd}
                    onTouchCancel={handleTouchCancel}
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

        </motion.div>

        <div
          className={`mt-5 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 rounded-[8px] border border-white/10 bg-panel p-4 md:p-5 ${
            item.type !== 'embed' ? 'h-[142px] sm:h-[147px] md:h-[170px]' : ''
          }`}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          {item.type === 'embed' ? (
            <p className='text-sm text-white/60'>{counter}</p>
          ) : (
            <div className='min-w-0 pr-2 sm:pr-5'>
              <h3 className='media-carousel-title w-full text-[24px] font-bold leading-tight text-white sm:text-[28px] md:text-[32px]'>
                {item.title}
              </h3>
            </div>
          )}

          <div className='flex shrink-0 flex-col items-end gap-3 justify-self-end'>
            {item.type !== 'embed' && (
              <p className='text-sm font-semibold text-accent'>{counter}</p>
            )}
            {controls}
          </div>
        </div>
      </div>
    </section>
  );
};

const Work = ({ selectedTrickRequest, selectedShowRequest, selectedVideoRequest }) => {
  return (
    <>
      <MediaCarousel
        items={trickItems}
        label='Tricks'
        description='FMX trick shots'
        sectionId='tricks'
        selectedItemRequest={selectedTrickRequest}
      />
      <MediaCarousel
        items={showItems}
        label='Shows'
        description='Live event moments'
        sectionId='shows'
        selectedItemRequest={selectedShowRequest}
      />
      <MediaCarousel
        items={videoItems}
        label='Videos'
        description='Short clips'
        sectionId='videos'
        autoPlay={false}
        selectedItemRequest={selectedVideoRequest}
      />
    </>
  );
};

export default Work;
